// src/utils/formatCurrency.js
// 🆕 [MULTI-DEVISES] Formatage des montants selon devise
// Utilise Intl.NumberFormat natif

/**
 * Formate un montant converti selon les règles de la devise
 * @param {number} amount - Montant déjà converti
 * @param {object} currency - { code, symbol, symbolPosition, decimals }
 * @param {string} locale - Locale utilisateur (ex: 'fr-FR')
 */
export function formatCurrency(amount, currency, locale = 'fr-FR') {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '—';
  }

  const decimals = currency?.decimals ?? 2;
  const code = currency?.code || 'USD';

  try {
    // Tente d'utiliser Intl natif (plus fiable pour les devises ISO)
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return formatter.format(amount);
  } catch (e) {
    // Fallback manuel si Intl ne connaît pas la devise
    return formatManual(amount, currency);
  }
}

/**
 * Formatage manuel (fallback)
 */
export function formatManual(amount, currency) {
  if (amount === null || amount === undefined || isNaN(amount)) return '—';

  const decimals = currency?.decimals ?? 2;
  const symbol = currency?.symbol || '';
  const position = currency?.symbolPosition || 'after';
  const thousandsSep = currency?.thousandsSep || ' ';
  const decimalSep = currency?.decimalSep || ',';

  const fixed = Number(amount).toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
  const numberStr = decPart ? `${intFormatted}${decimalSep}${decPart}` : intFormatted;

  if (position === 'before') {
    return `${symbol}${numberStr}`;
  }
  return `${numberStr} ${symbol}`;
}

/**
 * Format compact "100 USDT ≈ 58 489 FCFA"
 */
export function formatDual(balanceUSDT, display, locale = 'fr-FR') {
  const usdtStr = `${Number(balanceUSDT || 0).toFixed(2)} USDT`;
  if (!display || !display.currency || display.currency === 'USDT') {
    return usdtStr;
  }
  const localStr = display.formatted || formatCurrency(display.amount, display, locale);
  return `${usdtStr} ≈ ${localStr}`;
}

/**
 * Formate le taux : "1 USDT = 584,89 FCFA"
 */
export function formatRate(rate, currency, locale = 'fr-FR') {
  if (!rate || !currency) return '—';
  const rateStr = formatCurrency(rate, currency, locale);
  return `1 USDT = ${rateStr}`;
}